import React, { useState } from "react";
import { Card, Tabs, Typography, Divider, List, Tag, Alert, Collapse } from "antd";
import { CarOutlined, BarcodeOutlined, BookOutlined, RocketOutlined, UserOutlined, BarChartOutlined, MailOutlined } from "@ant-design/icons";
import OrderpickingDocumentation from "./OrderpickingDocumentation";
import ShippingDocumentation from "./ShippingDocumentation";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

const Container = styled.div`
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const SectionTitle = styled(Title)`
  margin-top: 2rem !important;
  margin-bottom: 1rem !important;
`;

const SubTitle = styled(Title)`
  margin-top: 1.5rem !important;
  margin-bottom: 0.75rem !important;
`;

const CodeBlock = styled.pre`
  background: #f5f5f5;
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
  border-left: 4px solid #1890ff;
`;

const StepList = styled(List)`
  .ant-list-item {
    padding: 0.75rem 0;
  }
`;

const Documentation = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("shipping");

  const MarketingDocumentation = () => (
    <div>
      <Title level={2}>
        <RocketOutlined /> Marketing Module
      </Title>
      <Paragraph>
        De Marketing module helpt je om campagnes te beheren, UTM parameters te genereren, en de prestaties van je
        marketinginspanningen te volgen.
      </Paragraph>

      {/* Campagnes Sectie */}
      <SectionTitle level={3}>📊 Campagnes</SectionTitle>

      <SubTitle level={4}>Wat zijn campagnes?</SubTitle>
      <Paragraph>
        Campagnes zijn georganiseerde marketinginspanningen om specifieke doelen te bereiken, zoals nieuwe klanten
        werven, bestaande klanten behouden, of specifieke producten promoten.
      </Paragraph>

      <SubTitle level={4}>Een campagne aanmaken</SubTitle>
      <StepList
        size="small"
        dataSource={[
          {
            title: "Navigeer naar Marketing → Campagnes",
            description: "Klik op 'Campagne toevoegen' in de rechterbovenhoek",
          },
          {
            title: "Vul de basis informatie in",
            description: "Campagne naam, platform (Facebook Ads, Google Ads, etc.), status (Actief, Concept, etc.)",
          },
          {
            title: "Stel doelen en budget in",
            description:
              "Kies het campagne type (Product korting, Seizoensgebonden, etc.) en doel (Acquisitie, Retentie, etc.)",
          },
          {
            title: "Configureer UTM parameters",
            description: "Vul utm_source, utm_medium, utm_campaign in voor tracking",
          },
          {
            title: "Voeg attributie methoden toe (optioneel)",
            description: "Kortingscodes, doelproducten, doelcategorieën, kanalen",
          },
          {
            title: "Sla de campagne op",
            description: "De campagne is nu klaar en kan worden geactiveerd",
          },
        ]}
        renderItem={(item) => (
          <List.Item>
            <List.Item.Meta title={<Text strong>{item.title}</Text>} description={item.description} />
          </List.Item>
        )}
      />

      <SubTitle level={4}>Campagne velden uitleg</SubTitle>
      <Collapse>
        <Panel header="Basis Informatie" key="1">
          <List size="small">
            <List.Item>
              <Text strong>Campagne naam:</Text> Een unieke, beschrijvende naam voor je campagne
            </List.Item>
            <List.Item>
              <Text strong>Platform:</Text> Waar de campagne wordt uitgevoerd (Facebook Ads, Google Ads, Instagram,
              Email, etc.)
            </List.Item>
            <List.Item>
              <Text strong>Status:</Text>
              <ul>
                <li>
                  <Tag color="default">Concept</Tag> - Campagne in voorbereiding
                </li>
                <li>
                  <Tag color="success">Actief</Tag> - Campagne is live
                </li>
                <li>
                  <Tag color="warning">Gepauzeerd</Tag> - Tijdelijk gestopt
                </li>
                <li>
                  <Tag color="error">Voltooid</Tag> - Campagne is afgelopen
                </li>
              </ul>
            </List.Item>
          </List>
        </Panel>

        <Panel header="Campagne Type & Doel" key="2">
          <List size="small">
            <List.Item>
              <Text strong>Type:</Text>
              <ul>
                <li>
                  <Tag>Product korting</Tag> - Kortingen op specifieke producten
                </li>
                <li>
                  <Tag>Seizoensgebonden</Tag> - Seizoenscampagnes (Kerst, Pasen, BBQ, etc.)
                </li>
                <li>
                  <Tag>Upsell</Tag> - Hogere waarde producten promoten
                </li>
                <li>
                  <Tag>Voorraad legen</Tag> - Overtollige voorraad verkopen
                </li>
              </ul>
            </List.Item>
            <List.Item>
              <Text strong>Doel:</Text>
              <ul>
                <li>
                  <Tag color="green">Acquisitie</Tag> - Nieuwe klanten werven
                </li>
                <li>
                  <Tag color="blue">Retentie</Tag> - Bestaande klanten behouden
                </li>
              </ul>
            </List.Item>
          </List>
        </Panel>

        <Panel header="UTM Parameters" key="3">
          <Paragraph>UTM parameters worden gebruikt om traffic te tracken en toe te wijzen aan je campagne.</Paragraph>
          <List size="small">
            <List.Item>
              <Text strong>utm_source:</Text> De bron van je traffic (bijv. facebook, google, newsletter)
            </List.Item>
            <List.Item>
              <Text strong>utm_medium:</Text> Het medium (bijv. cpc, email, social)
            </List.Item>
            <List.Item>
              <Text strong>utm_campaign:</Text> De campagne naam (bijv. lente_uitverkoop)
            </List.Item>
            <List.Item>
              <Text strong>utm_term:</Text> (Optioneel) Zoekwoorden voor betaalde zoekadvertenties
            </List.Item>
            <List.Item>
              <Text strong>utm_content:</Text> (Optioneel) Om verschillende advertenties te onderscheiden
            </List.Item>
          </List>
          <Alert
            message="Automatische URL generatie"
            description="Het systeem genereert automatisch een volledige UTM URL die je kunt kopiëren en gebruiken in je advertenties."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </Panel>

        <Panel header="Attributie Methoden" key="4">
          <Paragraph>Attributie methoden helpen het systeem om orders aan je campagne te koppelen.</Paragraph>
          <List size="small">
            <List.Item>
              <Text strong>Kortingscodes:</Text> Voeg kortingscodes toe die specifiek voor deze campagne zijn. Orders
              met deze codes worden automatisch aan de campagne gekoppeld.
            </List.Item>
            <List.Item>
              <Text strong>Doelproducten:</Text> Voeg product ID's toe die je wilt promoten. Orders met deze producten
              kunnen aan de campagne worden gekoppeld.
            </List.Item>
            <List.Item>
              <Text strong>Doelcategorieën:</Text> Voeg categorieën toe die je wilt targeten.
            </List.Item>
            <List.Item>
              <Text strong>Kanalen:</Text> Selecteer de kanalen waar deze campagne actief is.
            </List.Item>
          </List>
        </Panel>
      </Collapse>

      <SubTitle level={4}>Campagne prestaties bekijken</SubTitle>
      <Paragraph>Klik op een campagne om de detail pagina te openen. Hier zie je:</Paragraph>
      <List size="small">
        <List.Item>
          <Text strong>Real-time Metrics:</Text> Totaal orders, omzet, gemiddelde bestelwaarde, nieuwe klanten, etc.
        </List.Item>
        <List.Item>
          <Text strong>Performance Tab:</Text> Grafieken met omzet over tijd en attributie methoden breakdown
        </List.Item>
        <List.Item>
          <Text strong>Products Tab:</Text> Top verkochte producten en cross-sell analyse (producten vaak samen gekocht)
        </List.Item>
      </List>

      <Alert
        message="Order Attributie"
        description="Orders worden automatisch aan campagnes gekoppeld via UTM parameters, kortingscodes, of product targeting. Dit gebeurt via cloud functions wanneer een order wordt geplaatst."
        type="success"
        showIcon
        style={{ marginTop: 16 }}
      />

      <Divider />

      {/* UTM Tool Sectie */}
      <SectionTitle level={3}>🔗 UTM Tool</SectionTitle>

      <SubTitle level={4}>Wat is de UTM Tool?</SubTitle>
      <Paragraph>
        De UTM Tool helpt je om snel UTM parameters te genereren en op te slaan voor gebruik in je marketingcampagnes.
        Je kunt UTM's aanmaken zonder een volledige campagne te maken.
      </Paragraph>

      <SubTitle level={4}>Een UTM aanmaken</SubTitle>
      <StepList
        size="small"
        dataSource={[
          {
            title: "Navigeer naar Marketing → UTM Tool",
            description: "Klik op 'UTM Aanmaken'",
          },
          {
            title: "Vul de UTM naam in",
            description: "Geef de UTM een beschrijvende naam (bijv. 'Facebook Lente Sale')",
          },
          {
            title: "Selecteer Source en Medium",
            description: "Kies uit voorgedefinieerde opties of voer custom waarden in",
          },
          {
            title: "Vul Campaign, Term en Content in",
            description: "Campaign is verplicht, Term en Content zijn optioneel",
          },
          {
            title: "Bekijk de gegenereerde URL",
            description: "De volledige URL wordt automatisch gegenereerd",
          },
          {
            title: "Sla de UTM op",
            description: "De UTM wordt opgeslagen en kan later worden hergebruikt",
          },
        ]}
        renderItem={(item) => (
          <List.Item>
            <List.Item.Meta title={<Text strong>{item.title}</Text>} description={item.description} />
          </List.Item>
        )}
      />

      <SubTitle level={4}>UTM Best Practices</SubTitle>
      <List size="small">
        <List.Item>
          <Text strong>Gebruik consistente naming:</Text> Gebruik altijd lowercase en underscores (bijv. lente_sale,
          niet Lente Sale)
        </List.Item>
        <List.Item>
          <Text strong>Wees specifiek:</Text> Gebruik beschrijvende namen die duidelijk maken waar de traffic vandaan
          komt
        </List.Item>
        <List.Item>
          <Text strong>Documenteer je UTM's:</Text> Houd een overzicht bij van welke UTM's je gebruikt voor welke
          campagnes
        </List.Item>
        <List.Item>
          <Text strong>Test je links:</Text> Test altijd je UTM links voordat je ze gebruikt in live campagnes
        </List.Item>
      </List>

      <SubTitle level={4}>Opgeslagen UTM's gebruiken</SubTitle>
      <Paragraph>In de "Opgeslagen UTM's" sectie zie je alle eerder aangemaakte UTM's. Je kunt:</Paragraph>
      <List size="small">
        <List.Item>✅ De volledige URL kopiëren</List.Item>
        <List.Item>✏️ UTM bewerken</List.Item>
        <List.Item>🗑️ UTM verwijderen</List.Item>
        <List.Item>🚀 Direct een campagne aanmaken met deze UTM</List.Item>
      </List>

      <SubTitle level={4}>UTM naar Campagne</SubTitle>
      <Paragraph>
        Je kunt een opgeslagen UTM direct omzetten naar een volledige campagne door op "Campagne maken met UTM" te
        klikken. De UTM parameters worden automatisch ingevuld in het campagne formulier.
      </Paragraph>

      <Alert
        message="UTM Tracking in Webshop"
        description="UTM parameters worden automatisch gedetecteerd wanneer een klant via een UTM link de webshop bezoekt. Deze parameters worden 30 dagen bewaard en gekoppeld aan eventuele orders."
        type="info"
        showIcon
        style={{ marginTop: 16 }}
      />
    </div>
  );

  const SegmentationDocumentation = () => (
    <div>
      <Title level={2}>
        <UserOutlined /> Klantsegmentatie & RFM Analyse
      </Title>
      <Paragraph>
        De Klantsegmentatie module helpt je om klanten te groeperen op basis van hun koopgedrag en waarde voor je
        bedrijf.
      </Paragraph>

      {/* RFM Analyse Sectie */}
      <SectionTitle level={3}>📈 RFM Analyse</SectionTitle>

      <SubTitle level={4}>Wat is RFM?</SubTitle>
      <Paragraph>
        RFM staat voor <Text strong>Recency</Text>, <Text strong>Frequency</Text>, en <Text strong>Monetary</Text>. Het
        is een bewezen methode om klanten te segmenteren op basis van:
      </Paragraph>
      <List size="small">
        <List.Item>
          <Text strong>Recency (R):</Text> Hoe recent heeft de klant een aankoop gedaan?
        </List.Item>
        <List.Item>
          <Text strong>Frequency (F):</Text> Hoe vaak koopt de klant?
        </List.Item>
        <List.Item>
          <Text strong>Monetary (M):</Text> Hoeveel geeft de klant uit?
        </List.Item>
      </List>

      <SubTitle level={4}>RFM Analyse uitvoeren</SubTitle>
      <StepList
        size="small"
        dataSource={[
          {
            title: "Navigeer naar Reports → RFM Analyse",
            description: "Open het RFM Analyse Dashboard",
          },
          {
            title: "Selecteer een periode",
            description: "Kies de analyseperiode (bijv. laatste 90 dagen, laatste 180 dagen, laatste jaar)",
          },
          {
            title: "Selecteer shop source",
            description: "Kies VCE, HVO, of beide",
          },
          {
            title: "Klik op 'Nieuwe analyse uitvoeren'",
            description: "Het systeem analyseert alle orders en klanten in de geselecteerde periode",
          },
          {
            title: "Wacht op voltooiing",
            description: "De analyse kan enkele seconden tot minuten duren,afhankelijk van het aantal orders",
          },
          {
            title: "Bekijk de resultaten",
            description: "De analyse wordt opgeslagen en verschijnt in de analyse geschiedenis",
          },
        ]}
        renderItem={(item) => (
          <List.Item>
            <List.Item.Meta title={<Text strong>{item.title}</Text>} description={item.description} />
          </List.Item>
        )}
      />

      <SubTitle level={4}>RFM Scores</SubTitle>
      <Paragraph>Elke klant krijgt een score van 1-5 voor elk van de drie dimensies:</Paragraph>
      <List size="small">
        <List.Item>
          <Tag color="green">Score 5:</Tag> Beste klanten (meest recent, meest frequent, hoogste uitgaven)
        </List.Item>
        <List.Item>
          <Tag color="blue">Score 4:</Tag> Zeer goede klanten
        </List.Item>
        <List.Item>
          <Tag color="cyan">Score 3:</Tag> Gemiddelde klanten
        </List.Item>
        <List.Item>
          <Tag color="orange">Score 2:</Tag> Onder gemiddelde klanten
        </List.Item>
        <List.Item>
          <Tag color="red">Score 1:</Tag> Minst waardevolle klanten
        </List.Item>
      </List>

      <SubTitle level={4}>RFM Segmenten</SubTitle>
      <Paragraph>Op basis van de RFM scores worden klanten automatisch ingedeeld in segmenten:</Paragraph>
      <Collapse>
        <Panel
          header={
            <>
              <Tag color="gold">Champions</Tag> - Beste klanten
            </>
          }
          key="1"
        >
          <Paragraph>
            <Text strong>RFM Score:</Text> 555, 554, 544, 545, 454, 455, 445
          </Paragraph>
          <Paragraph>
            <Text strong>Kenmerken:</Text> Kopen recent, vaak, en geven veel uit
          </Paragraph>
          <Paragraph>
            <Text strong>Marketing strategie:</Text> Beloon ze, vraag om reviews, early access tot nieuwe producten
          </Paragraph>
        </Panel>

        <Panel
          header={
            <>
              <Tag color="green">Loyal Customers</Tag> - Loyale klanten
            </>
          }
          key="2"
        >
          <Paragraph>
            <Text strong>RFM Score:</Text> 543, 444, 435, 355, 354, 345, 344, 335
          </Paragraph>
          <Paragraph>
            <Text strong>Kenmerken:</Text> Kopen regelmatig en geven gemiddeld tot veel uit
          </Paragraph>
          <Paragraph>
            <Text strong>Marketing strategie:</Text> Upsell hogere waarde producten, loyaliteitsprogramma
          </Paragraph>
        </Panel>

        <Panel
          header={
            <>
              <Tag color="blue">Potential Loyalists</Tag> - Potentieel loyaal
            </>
          }
          key="3"
        >
          <Paragraph>
            <Text strong>RFM Score:</Text> 553, 551, 552, 541, 542, 533, 532, 531, 452, 451, 442, 441, 431, 453, 433,
            432, 423, 353, 352, 351, 342, 341, 333, 323
          </Paragraph>
          <Paragraph>
            <Text strong>Kenmerken:</Text> Recent gekocht, goede frequentie, gemiddelde uitgaven
          </Paragraph>
          <Paragraph>
            <Text strong>Marketing strategie:</Text> Bied lidmaatschap aan, aanbeveel gerelateerde producten
          </Paragraph>
        </Panel>

        <Panel
          header={
            <>
              <Tag color="cyan">Recent Customers</Tag> - Recente klanten
            </>
          }
          key="4"
        >
          <Paragraph>
            <Text strong>RFM Score:</Text> 512, 511, 422, 421, 412, 411, 311
          </Paragraph>
          <Paragraph>
            <Text strong>Kenmerken:</Text> Recent gekocht, lage frequentie
          </Paragraph>
          <Paragraph>
            <Text strong>Marketing strategie:</Text> Onboarding, educatie, tweede aankoop stimuleren
          </Paragraph>
        </Panel>

        <Panel
          header={
            <>
              <Tag color="purple">Promising</Tag> - Veelbelovend
            </>
          }
          key="5"
        >
          <Paragraph>
            <Text strong>RFM Score:</Text> 525, 524, 523, 522, 521, 515, 514, 513, 425,424, 413,414,415, 315, 314, 313
          </Paragraph>
          <Paragraph>
            <Text strong>Kenmerken:</Text> Recent gekocht, lage tot gemiddelde uitgaven
          </Paragraph>
          <Paragraph>
            <Text strong>Marketing strategie:</Text> Creëer brand awareness, gratis aanbiedingen
          </Paragraph>
        </Panel>

        <Panel
          header={
            <>
              <Tag color="orange">Need Attention</Tag> - Aandacht nodig
            </>
          }
          key="6"
        >
          <Paragraph>
            <Text strong>RFM Score:</Text> 535, 534, 443, 434, 343, 334, 325, 324
          </Paragraph>
          <Paragraph>
            <Text strong>Kenmerken:</Text> Gemiddelde recency, goede frequentie en waarde
          </Paragraph>
          <Paragraph>
            <Text strong>Marketing strategie:</Text> Tijdelijke aanbiedingen, nieuwe producten tonen
          </Paragraph>
        </Panel>

        <Panel
          header={
            <>
              <Tag color="volcano">About to Sleep</Tag> - Dreigen te slapen
            </>
          }
          key="7"
        >
          <Paragraph>
            <Text strong>RFM Score:</Text> 331, 321, 312, 221, 213, 231, 241, 251
          </Paragraph>
          <Paragraph>
            <Text strong>Kenmerken:</Text> Onder gemiddelde recency, frequentie en waarde
          </Paragraph>
          <Paragraph>
            <Text strong>Marketing strategie:</Text> Reactiver met relevante aanbiedingen
          </Paragraph>
        </Panel>

        <Panel
          header={
            <>
              <Tag color="red">At Risk</Tag> - Risico klanten
            </>
          }
          key="8"
        >
          <Paragraph>
            <Text strong>RFM Score:</Text> 255, 254, 245, 244, 253, 252, 243, 242, 235, 234, 225, 224, 153, 152, 145,
            143, 142, 135, 134, 133, 125, 124
          </Paragraph>
          <Paragraph>
            <Text strong>Kenmerken:</Text> Lange tijd niet gekocht, maar waren voorheen goede klanten
          </Paragraph>
          <Paragraph>
            <Text strong>Marketing strategie:</Text> Win-back campagnes, vraag om feedback
          </Paragraph>
        </Panel>

        <Panel
          header={
            <>
              <Tag color="magenta">Can't Lose Them</Tag> - Niet verliezen
            </>
          }
          key="9"
        >
          <Paragraph>
            <Text strong>RFM Score:</Text> 155, 154, 144, 214,215,115, 114, 113
          </Paragraph>
          <Paragraph>
            <Text strong>Kenmerken:</Text> Waren Champions, maar kopen al lang niet meer
          </Paragraph>
          <Paragraph>
            <Text strong>Marketing strategie:</Text> Agressieve win-back, persoonlijk contact
          </Paragraph>
        </Panel>

        <Panel
          header={
            <>
              <Tag>Hibernating</Tag> - Slapend
            </>
          }
          key="10"
        >
          <Paragraph>
            <Text strong>RFM Score:</Text> 332, 322, 233, 232, 223, 222, 132, 123, 122, 212, 211
          </Paragraph>
          <Paragraph>
            <Text strong>Kenmerken:</Text> Lange tijd niet gekocht, lage waarde
          </Paragraph>
          <Paragraph>
            <Text strong>Marketing strategie:</Text> Lage prioriteit, goedkope reactivatie pogingen
          </Paragraph>
        </Panel>

        <Panel
          header={
            <>
              <Tag color="default">Lost</Tag> - Verloren
            </>
          }
          key="11"
        >
          <Paragraph>
            <Text strong>RFM Score:</Text> 111, 112, 121, 131,141,151
          </Paragraph>
          <Paragraph>
            <Text strong>Kenmerken:</Text> Zeer lang niet gekocht, lage waarde
          </Paragraph>
          <Paragraph>
            <Text strong>Marketing strategie:</Text> Negeer of verwijder uit database
          </Paragraph>
        </Panel>
      </Collapse>

      <Divider />

      {/* Customer Segments Sectie */}
      <SectionTitle level={3}>👥 Klantsegmenten Dashboard</SectionTitle>

      <SubTitle level={4}>Dashboard gebruiken</SubTitle>
      <Paragraph>
        Het Klantsegmenten Dashboard (Reports → Klantsegmenten) toont een overzicht van alle segmenten en hun
        ontwikkeling.
      </Paragraph>

      <SubTitle level={4}>Dashboard features</SubTitle>
      <List size="small">
        <List.Item>
          <Text strong>Segment overzicht:</Text> Zie hoeveel klanten in elk segment zitten
        </List.Item>
        <List.Item>
          <Text strong>Trend grafieken:</Text> Volg de ontwikkeling van segmenten over tijd
        </List.Item>
        <List.Item>
          <Text strong>Segment details:</Text> Bekijk gemiddelde bestelwaarde, recency, en andere metrics per segment
        </List.Item>
        <List.Item>
          <Text strong>Filters:</Text> Filter op shop source, periode, en marketing focus
        </List.Item>
        <List.Item>
          <Text strong>Export:</Text> Exporteer segmenten naar CSV of Mailchimp
        </List.Item>
      </List>

      <SubTitle level={4}>Segment Export</SubTitle>
      <Paragraph>
        Klik op "Segment exporteren" om klanten te exporteren voor gebruik in email marketing of andere tools.
      </Paragraph>
      <StepList
        size="small"
        dataSource={[
          {
            title: "Kies export formaat",
            description: "CSV (algemeen) of Mailchimp (direct importeerbaar)",
          },
          {
            title: "Pas filters toe",
            description: "Selecteer segment, shop, min/max orders, min/max uitgegeven, recency, campagne interactie",
          },
          {
            title: "Bekijk preview",
            description: "Zie hoeveel klanten worden geëxporteerd en hun totale waarde",
          },
          {
            title: "Download",
            description: "Het bestand wordt direct gedownload naar je computer",
          },
        ]}
        renderItem={(item) => (
          <List.Item>
            <List.Item.Meta title={<Text strong>{item.title}</Text>} description={item.description} />
          </List.Item>
        )}
      />

      <Alert
        message="GDPR Compliance"
        description="Wees voorzichtig met geëxporteerde klantdata. Zorg dat je toestemming hebt om deze data te gebruiken en beveilig de bestanden goed."
        type="warning"
        showIcon
        style={{ marginTop: 16 }}
      />

      <SubTitle level={4}>Mailchimp Export</SubTitle>
      <Paragraph>Bij Mailchimp export wordt het bestand geformatteerd volgens Mailchimp's vereisten:</Paragraph>
      <CodeBlock>
        {`Email Address,First Name,Last Name,Tags
customer@example.com,John,Doe,Champions;VCE
another@example.com,Jane,Smith,Loyal Customers;HVO`}
      </CodeBlock>
      <Paragraph>Tags worden automatisch toegevoegd voor het RFM segment en de shop source.</Paragraph>

      <SubTitle level={4}>Best Practices</SubTitle>
      <List size="small">
        <List.Item>
          <Text strong>Voer regelmatig RFM analyses uit:</Text> Minimaal eens per maand om segmenten up-to-date te
          houden
        </List.Item>
        <List.Item>
          <Text strong>Gebruik segmenten voor targeted marketing:</Text> Stuur verschillende boodschappen naar
          verschillende segmenten
        </List.Item>
        <List.Item>
          <Text strong>Monitor segment beweging:</Text> Let op klanten die van Champions naar At Risk gaan
        </List.Item>
        <List.Item>
          <Text strong>Combineer met campagne data:</Text> Zie welke campagnes welke segmenten aantrekken
        </List.Item>
      </List>
    </div>
  );

  const ReactivationDocumentation = () => (
    <div>
      <Title level={2}>
        <MailOutlined /> Reactivatie / Win-back systeem
      </Title>
      <Paragraph>
        Uit de marketing-metrics blijkt dat bijna de helft van de klanten maar <Text strong>één keer</Text> bestelt. Het
        reactivatiesysteem stuurt deze en andere klanten automatisch een gerichte win-back mail, met volledig inzicht in
        wélke mail naar wie is gestuurd en wat het oplevert (Campagne Rapportage, zie onderaan).
      </Paragraph>

      <Alert
        message="Volledig geautomatiseerd"
        description="Dit systeem draait via een scheduled cloud function (elke dag 09:00, Europe/Amsterdam) — er is geen handmatige actie nodig om mails te versturen. Campagnes aanmaken/pauzeren doe je wel handmatig in de CMS (zie 'Campagnes koppelen' onderaan)."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <SectionTitle level={3}>📬 Welke mail gaat naar welk segment, en wanneer?</SectionTitle>
      <Paragraph>
        Er zijn twee geautomatiseerde win-back reeksen live. Voor beide geldt: een klant komt pas in aanmerking als hij
        niet op de <Text strong>unsubscribe-lijst</Text> staat, niet recent al dezelfde mail kreeg, geen actieve
        persoonlijke korting heeft, en (voor eenmalige kopers) nog steeds niet opnieuw heeft besteld.
      </Paragraph>

      <Collapse defaultActiveKey={["single-buyer"]}>
        <Panel
          header={
            <>
              <Tag color="gold">Eenmalige Kopers</Tag> — drie oplopende win-back mails
            </>
          }
          key="single-buyer"
        >
          <Paragraph>
            <Text strong>Doelgroep:</Text> klanten met <Text strong>precies 1 bestelling ooit</Text> (levenslang, niet
            per periode). Dit wordt rechtstreeks gecontroleerd tegen de orders van de klant — <Text strong>niet</Text>{" "}
            via het RFM-segment "Eenmalige Kopers" uit de Klantsegmentatie-module hiernaast, want dat RFM-segment geldt
            alleen zolang de laatste bestelling ≤ 64 dagen geleden is. Daarna is voor déze win-back reeks alleen
            relevant hoeveel <Text strong>dagen sinds die ene bestelling</Text> zijn verstreken.
          </Paragraph>
          <Paragraph>
            <Text strong>Belangrijk:</Text> in de eerste 3 maanden na de bestelling gebeurt er bewust niets. Het eerste
            contactmoment is bij 3 maanden inactiviteit.
          </Paragraph>
          <List size="small">
            <List.Item>
              <Tag color="blue">Tier 1</Tag> <Text strong>3–6 maanden</Text> sinds de enige bestelling →{" "}
              <Text strong>10% korting</Text>. Wordt maximaal 1x per klant verstuurd.
            </List.Item>
            <List.Item>
              <Tag color="orange">Tier 2</Tag> <Text strong>6–12 maanden</Text> → <Text strong>15% korting</Text>. Ook
              maximaal 1x per klant.
            </List.Item>
            <List.Item>
              <Tag color="red">Tier 3</Tag> <Text strong>12+ maanden</Text> → <Text strong>20% korting</Text>. Deze
              herhaalt — zolang de klant nog steeds maar 1 bestelling heeft, komt hij hiervoor elke ~180 dagen
              (instelbaar) opnieuw in aanmerking.
            </List.Item>
          </List>
          <Paragraph style={{ marginTop: 12 }}>
            Zodra een klant opnieuw bestelt, valt hij per direct uit alle drie de tiers — hij is dan geen "eenmalige
            koper" meer.
          </Paragraph>
        </Panel>

        <Panel
          header={
            <>
              <Tag color="purple">Slapende Trouwe Klanten</Tag> — persoonlijke win-back
            </>
          }
          key="loyal-sleeper"
        >
          <Paragraph>
            <Text strong>Doelgroep:</Text> klanten die in het RFM-segment "Slapende Trouwe Klanten" vallen — voorheen
            frequente, hoogwaardige klanten die inactief zijn geworden (zie Klantsegmentatie-tab voor de exacte
            RFM-scores).
          </Paragraph>
          <Paragraph>
            <Text strong>Wanneer:</Text> zodra een klant dit segment binnenkomt (bepaald door de nachtelijke
            RFM-herberekening). Er is geen dag-venster zoals bij eenmalige kopers.
          </Paragraph>
          <Paragraph>
            <Text strong>Korting:</Text> persoonlijke korting (standaard 10%). De mail herhaalt met een cooldown van
            standaard 90 dagen, zolang de klant in dit segment blijft.
          </Paragraph>
        </Panel>

        <Panel
          header={
            <>
              <Tag>Overige RFM-segmenten</Tag> — nog niet geautomatiseerd
            </>
          }
          key="future"
        >
          <Paragraph>
            Belovende Nieuwkomers, Loyale Klanten, Slapende Gewone Klanten, Verloren Klanten en Champions/Big Spenders
            hebben nog geen geautomatiseerde mailflow (staat gepland, nog niet gebouwd). Voor deze segmenten kun je nu
            alleen handmatig exporteren via de Klantsegmentatie-module en zelf mailen.
          </Paragraph>
        </Panel>
      </Collapse>

      <SectionTitle level={3}>🎁 Persoonlijke korting</SectionTitle>
      <Paragraph>
        Bij elke win-back mail (behalve als er al handmatig een kortingscode is meegegeven) maakt het systeem een
        unieke, persoonlijke kortingscode aan voor die klant.
      </Paragraph>
      <List size="small">
        <List.Item>
          <Text strong>Ingelogde klant:</Text> de korting wordt automatisch getoond en toegepast in winkelmand/checkout
          — de klant hoeft niets in te typen.
        </List.Item>
        <List.Item>
          <Text strong>Gast (niet ingelogd):</Text> kan de code uit de mail wel handmatig invoeren bij checkout.
        </List.Item>
        <List.Item>
          <Text strong>Eenmalig gebruik:</Text> de code werkt precies 1 keer, in totaal — niet per klant of adres. Na
          gebruik wordt de kortingscode zelf verwijderd.
        </List.Item>
        <List.Item>
          <Text strong>Max. 1 actieve korting per klant:</Text> krijgt een klant een nieuwe win-back mail terwijl er nog
          een oude persoonlijke korting openstaat, dan vervalt de oude automatisch.
        </List.Item>
        <List.Item>
          <Text strong>Geldigheid:</Text> 60 dagen na verzending; de laatste geldige dag staat in de mail.
        </List.Item>
      </List>

      <SectionTitle level={3}>🔗 Campagnes koppelen (CMS)</SectionTitle>
      <Paragraph>
        Om een van bovenstaande mails daadwerkelijk te laten versturen, moet er een <Text strong>actieve</Text> campagne
        bestaan in Marketing → Campagnes met het juiste mailtype:
      </Paragraph>
      <List size="small">
        <List.Item>
          <Text code>single_buyer_winback_90</Text> — Tier 1 (3–6 maanden, 10%)
        </List.Item>
        <List.Item>
          <Text code>single_buyer_winback_180</Text> — Tier 2 (6–12 maanden, 15%)
        </List.Item>
        <List.Item>
          <Text code>single_buyer_winback_365</Text> — Tier 3 (12+ maanden, 20%)
        </List.Item>
        <List.Item>
          <Text code>loyal_sleeper_winback</Text> — Slapende Trouwe Klanten
        </List.Item>
      </List>
      <Alert
        message="Zonder actieve campagne wordt er niets verstuurd"
        description="Ontbreekt er voor een mailtype een actieve campagne, dan slaat de dagelijkse functie dat segment die dag gewoon over (zichtbaar in de cloud function-logs als 'no active campaign for ... , skipping')."
        type="warning"
        showIcon
        style={{ marginTop: 8 }}
      />

      <SectionTitle level={3}>📊 Resultaten terugvinden</SectionTitle>
      <List size="small">
        <List.Item>
          <Text strong>Campagne Rapportage</Text> (Reports → Campagne Rapportage): verzonden, conversie, omzet en de
          kortingsfunnel (uitgegeven/ingewisseld/actief/verlopen) per segment, mailtype en campagne. Dit is de bron van
          waarheid voor deze win-back mails.
        </List.Item>
        <List.Item>
          <Text strong>Campagnedetail</Text> (klik op een campagne in Marketing → Campagnes): dezelfde brondata, maar
          gefilterd op 1 specifieke campagne — inclusief top-producten en cross-sell binnen die campagne.
        </List.Item>
      </List>
      <Paragraph>
        Beide pagina's lezen dezelfde onderliggende data (<Text code>campaignSends</Text> en{" "}
        <Text code>personalDiscounts</Text>), niet de generieke campagne-attributie die de Marketing-tab hiernaast
        beschrijft — unieke persoonlijke kortingscodes staan namelijk nooit vooraf op een campagne genoteerd, dus die
        generieke matching mist deze mails.
      </Paragraph>
    </div>
  );

  const tabItems = [
    {
      key: "shipping",
      label: (
        <span>
          <CarOutlined /> Verzenden
        </span>
      ),
      children: <ShippingDocumentation />,
    },
    {
      key: "marketing",
      label: (
        <span>
          <RocketOutlined /> Marketing
        </span>
      ),
      children: <MarketingDocumentation />,
    },
    {
      key: "segmentation",
      label: (
        <span>
          <UserOutlined /> Klantsegmentatie & RFM
        </span>
      ),
      children: <SegmentationDocumentation />,
    },
    {
      key: "reactivation",
      label: (
        <span>
          <MailOutlined /> Reactivatie / Win-back
        </span>
      ),
      children: <ReactivationDocumentation />,
    },
    {
      key: "orderpicking",
      label: (
        <span>
          <BarcodeOutlined /> Orderpicken
        </span>
      ),
      children: <OrderpickingDocumentation />,
    },
  ];

  return (
    <Container>
      <Card>
        <Title level={1}>
          <BookOutlined /> CMS Documentatie
        </Title>
        <Paragraph>
          Welkom bij de CMS documentatie. Hier vind je uitgebreide handleidingen voor alle modules van het systeem.
        </Paragraph>

        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} size="large" />
      </Card>
    </Container>
  );
};

export default Documentation;
